import { ArrowRightOutlined } from "@ant-design/icons"
import { Button, Card, Drawer, Flex, Skeleton, Tooltip, Typography } from "antd"
import { useCallback, useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { useParams } from "react-router"

import { useAppDispatch } from "../../../../../../hooks/use-app-dispatch.ts"
import { useAppSelector } from "../../../../../../hooks/use-app-selector.ts"
import { budgetFormat } from "../../../../../../utils/number/budget-format.ts"
import { fetchFinancialSnapshot } from "../../../../redux/project-finance/project-finance.thunks.ts"
import type { FinancialKpiCell } from "../../../../types/project-finance.types.tsx"
import { ProjectViewFinanceSnapshot } from "./ProjectViewFinanceSnapshot/ProjectViewFinanceSnapshot.tsx"

const { Title, Text } = Typography

const SECTION_ACCENT: Record<string, string> = {
  income: "border-green-500",
  cost: "border-orange-500",
  profit: "border-primary"
}

interface ProjectViewFinanceKpiProps {
  showSalesSnapshot: boolean
  showCommissionSnapshot: boolean
}

function KpiTile({ cell }: { cell: FinancialKpiCell }) {
  const numeric = cell.value === "" ? Number.NaN : Number(cell.value)
  const absent = cell.has_data === false || !Number.isFinite(numeric)
  const negative = !absent && numeric < 0

  return (
    <div
      className="flex-1 min-w-0"
      data-testid={`kpi-${cell.key}`}
      data-negative={String(negative)}
    >
      <Tooltip title={cell.description}>
        <Text type="secondary" className="text-xs block truncate uppercase">
          {cell.title}
        </Text>
      </Tooltip>
      <Text
        className={`text-base font-semibold block ${negative ? "text-red-500" : ""}`}
      >
        {absent ? "—" : budgetFormat(numeric)}
      </Text>
      {cell.caption ? (
        <Text type="secondary" className="text-xs block truncate italic">
          {cell.caption}
        </Text>
      ) : null}
    </div>
  )
}

export function ProjectViewFinanceKpi({
  showSalesSnapshot,
  showCommissionSnapshot
}: ProjectViewFinanceKpiProps) {
  const projectId = useParams()?.id as string
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const [detailOpen, setDetailOpen] = useState(false)
  const [initialized, setInitialized] = useState(false)

  const snapshot = useAppSelector(
    (state) => state?.project?.financialTransactionSnapshot
  )

  const handleFetch = useCallback(async () => {
    await dispatch(fetchFinancialSnapshot(projectId))
    setInitialized(true)
  }, [dispatch, projectId])

  useEffect(() => {
    void handleFetch()
  }, [handleFetch])

  const kpi = snapshot?.kpi

  const sections = useMemo(() => {
    if (!kpi || !showSalesSnapshot) return []
    return kpi.rows.map((row) => ({
      ...row,
      cells: kpi.cells.filter((cell) => cell.row === row.key)
    }))
  }, [kpi, showSalesSnapshot])

  if (!kpi || !initialized) {
    return (
      <Card title={<Title level={5}>
          {t("financialSnapshot.title", "Financial Snapshot")}
        </Title>} size="small">
        <Skeleton.Node active className={"w-full h-40"} />
      </Card>
    )
  }

  return (
    <>
      <Card
        size="small"
        classNames={{ body: "p-2" }}
        title={
          <Flex justify="space-between" align="center" gap={8}>
            <Title level={5} className="!mb-0">
              {t("financialSnapshot.title", "Financial Snapshot")}
            </Title>
            <Flex align="center" gap={12}>
              <Text type="secondary" className="text-xs">
                {t("financialSnapshot.currencyNote", "All amounts in USD")}
              </Text>
              <Button
                type="link"
                size="small"
                className="!px-0"
                data-testid="kpi-view-details"
                onClick={() => setDetailOpen(true)}
              >
                {t("financialSnapshot.viewDetails", "View Details")}{" "}
                <ArrowRightOutlined />
              </Button>
            </Flex>
          </Flex>
        }
      >
        {showSalesSnapshot &&
          sections.map((section, index) => (
            <div
              key={section.key}
              className={`pl-2 border-l-2 ${SECTION_ACCENT[section.key] ?? ""} ${
                index > 0 ? "mt-2 pt-2 border-t border-t-gray-200" : ""
              }`}
            >
              <Text type="secondary" className="text-xs font-semibold">
                {section.title.toUpperCase()}
              </Text>
              <Flex gap={12} className="mt-1">
                {section.cells.map((cell) => (
                  <KpiTile key={cell.key} cell={cell} />
                ))}
              </Flex>
            </div>
          ))}
      </Card>

      <Drawer
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        width={720}
        title={t("financialSnapshot.detailTitle", "Financial Snapshot")}
        classNames={{ body: "p-2" }}
      >
        <div data-testid="finance-detail-drawer">
          {detailOpen ? (
            <ProjectViewFinanceSnapshot
              showSalesSnapshot={showSalesSnapshot}
              showCommissionSnapshot={showCommissionSnapshot}
            />
          ) : null}
        </div>
      </Drawer>
    </>
  )
}
